/**
 * Centralized API client that uses proxy routes to avoid CORS issues
 */

import { logError } from "@/utils/logError";

export type GlobalIncident = {
  id: number;
  title: string;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type ImageUploadResponse = {
  link: string;
};

/**
 * Custom error class that preserves API error details
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public apiMessage?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

class ApiClient {
  private async fetchWithCredentials(url: string, options: RequestInit = {}) {
    const response = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    if (!response.ok) {
      let apiMessage: string | undefined;
      const errorText = await response.text();

      // Try to parse as JSON to extract the error message
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          apiMessage = errorJson.error;
        }
      } catch {
        // Not JSON, use the raw text
        apiMessage = errorText;
      }

      const error = new ApiError(
        `API Error: ${response.status}`,
        response.status,
        apiMessage,
      );

      // Log error for observability
      logError("API request failed", error, {
        app: "get-help",
        feature: "api-client",
        extra: {
          url,
          status: response.status,
          apiMessage,
        },
      });

      throw error;
    }

    return response;
  }

  async getSupportEligibility(): Promise<any> {
    const response = await this.fetchWithCredentials(
      "/api/supportfrontend/v1/proxy/support-eligibility",
    );
    return response.json();
  }

  async getCurrentUser(): Promise<any> {
    try {
      const response = await this.fetchWithCredentials(
        "/api/supportfrontend/v1/proxy/current-user",
      );
      return response.json();
    } catch (error) {
      console.error("Failed to get current user:", error);
      return null;
    }
  }

  async createZendeskTicket(data: any): Promise<any> {
    const response = await this.fetchWithCredentials(
      "/api/supportfrontend/v1/proxy/zendesk/ticket",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return response.json();
  }

  async getAccountEntitlements(accountId: string): Promise<any> {
    const response = await this.fetchWithCredentials(
      `/api/supportfrontend/v1/proxy/entitlements?accountId=${encodeURIComponent(
        accountId,
      )}`,
    );
    return response.json();
  }

  async getGlobalIncidents(): Promise<GlobalIncident[]> {
    try {
      const response = await this.fetchWithCredentials(
        "/api/supportfrontend/v1/proxy/incidents",
      );
      const data = await response.json();
      return data.incidents || [];
    } catch (error) {
      console.error("Failed to get incidents:", error);
      return [];
    }
  }

  async uploadImage(imageData: {
    image: string;
  }): Promise<ImageUploadResponse> {
    const response = await this.fetchWithCredentials(
      "/api/supportfrontend/v1/proxy/image-upload",
      {
        method: "POST",
        body: JSON.stringify(imageData),
      },
    );
    return response.json();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
